import { whenAmazonTakt } from '@amzn/takt-utils';
import { ChromeComponent } from 'src/constants';

export const logWithTakt = (context: unknown, details: unknown): void => {
  whenAmazonTakt((amazonTakt) => {
    amazonTakt.logCustom({
      context,
      details: JSON.stringify(details),
    });
  });
};

export const logMetadata = (metadata: unknown): void => {
  whenAmazonTakt((amazonTakt) => {
    amazonTakt.addVisitMetadata(metadata);
  });
};

/**
 * Invokes `callback` each time Takt records a new visit for the current
 * document. Returns a function that stops the subscription.
 *
 * Takt mints a visit on every URL change, not just on document load: it wraps
 * `history.pushState` / `replaceState` and listens to `popstate` (TaktAsset
 * `takt.js`, `logVisit`). Each visit starts with fresh metadata, so anything
 * recorded with `addVisitMetadata` for the previous visit does not carry over.
 * Subscribers use this to re-record per-visit metadata after a client-side
 * route change.
 *
 * The callback fires synchronously inside Takt's history hook — before React
 * has rendered the new route — so subscribers must defer any DOM read.
 *
 * Takt exposes `registerVisitCallback` but no matching unregister, so the
 * subscription is retired by an `active` flag; the underlying observer is
 * removed as well where Takt exposes it, so it does not accumulate across
 * remounts.
 */
export const onVisit = (callback: () => void): (() => void) => {
  let active = true;
  const observer = (): void => {
    if (active) {
      callback();
    }
  };

  whenAmazonTakt((amazonTakt) => {
    if (typeof amazonTakt.registerVisitCallback === 'function') {
      amazonTakt.registerVisitCallback(observer);
    }
  });

  return () => {
    active = false;
    whenAmazonTakt((amazonTakt) => {
      amazonTakt.visitObservable?.removeObserver?.(observer);
    });
  };
};

export const markComponentStart = (component: string): void => {
  whenAmazonTakt((amazonTakt) => {
    amazonTakt.waitForComponentsReady('AACChrome', [component]);
    amazonTakt.markComponentReady(
      component,
      amazonTakt.ComponentReadyState.START,
    );
  });
};

export const markComponentReady = (component: string): void => {
  whenAmazonTakt((amazonTakt) => {
    amazonTakt.markComponentReady(
      component,
      amazonTakt.ComponentReadyState.READY,
    );
  });
};

export const initTakt = (): void => {
  whenAmazonTakt((amazonTakt) => {
    amazonTakt.markComponentReady(
      'AACChrome',
      amazonTakt.ComponentReadyState.START,
    );
  });
};

export const markAACChromeReady = (): void => {
  whenAmazonTakt((amazonTakt) => {
    amazonTakt.markComponentReady(
      'AACChrome',
      amazonTakt.ComponentReadyState.READY,
    );
  });
};

interface TaktAttributes {
  'data-takt-id': string;
  'data-takt-value'?: string;
}

export const getTaktAttributes = (
  id: string,
  component?: ChromeComponent,
  value?: Record<string, string>,
  allToolsEnabled?: boolean,
): TaktAttributes | undefined => {
  if (!id) {
    return undefined;
  }

  const componentPrefixes: Record<string, string> = {
    nav: allToolsEnabled ? 'nav_global_tools_' : 'nav_global_',
    allToolsNav: 'nav_tools_panel_',
    pivotNav: 'nav_pivot_',
    footer: 'footer_',
    header: 'header_',
  };

  const prefix = componentPrefixes[component ?? ''] || 'chrome_';
  const taktId =
    component === 'nav' && id.startsWith('aac-menu-item-')
      ? id.replace('aac-menu-item-', prefix)
      : prefix + id;

  return {
    'data-takt-id': taktId,
    ...(value && { 'data-takt-value': JSON.stringify(value) }),
  };
};
